import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "header" }
const _hoisted_2 = { class: "title" }
const _hoisted_3 = { class: "progress-bar__header" }
const _hoisted_4 = { class: "progress-bar__title" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { class: "progress-bar__value" }
const _hoisted_7 = { class: "project-values" }
const _hoisted_8 = { class: "row" }
const _hoisted_9 = { class: "label" }
const _hoisted_10 = { class: "value" }
const _hoisted_11 = { class: "row" }
const _hoisted_12 = { class: "label" }
const _hoisted_13 = {
  key: 0,
  class: "value"
}
const _hoisted_14 = { class: "row" }
const _hoisted_15 = { class: "label" }
const _hoisted_16 = { class: "row" }
const _hoisted_17 = { class: "label" }
const _hoisted_18 = { class: "value" }
const _hoisted_19 = {
  key: 0,
  class: "team-hours"
}
const _hoisted_20 = { class: "value" }

import { computed, ref, watch } from "vue";
import IconButton from "@/components/IconButton.vue";
import ProgressBar from "@/components/ProgressBar.vue";
import MainContentBase from "@/components/layout/MainContentBase.vue";
import BaseSheet from "@/BaseSheet.vue";
import ProgressItem from "@/components/ProgressItem.vue";
import { formatPrice, formatPercentage, formatFloat } from "@/helpers/utils";
import {
  provideApolloClient,
  useMutation,
  useQuery,
} from "@vue/apollo-composable";
import {
  GET_EMPLOYEE_PROJECT,
  GET_LOGGED_HOURS,
} from "@/graphql/project/queries";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import enUK from "@/locales/en-UK.json";
import projectEnUk from "@/locales/project/en-UK.json";
import dayjs from "dayjs";
import {
  LoggedHoursResult,
  WorkingPeriod,
} from "@/helpers/project/typesDefinition";
import { apolloClient } from "@/apollo.provider";
import { formatPeriodDate } from "@/helpers/order/utils";
import { Icon } from "@iconify/vue";
import { UPDATE_FAVORITE } from "@/graphql/project/mutations";
import { logErrorMessages } from "@vue/apollo-util";
import { useToast } from "vue-toastification";
import ProjectReportingThematic from "@/views/project/ProjectReportingThematic.vue";

type MessageSchema = typeof enUK | typeof projectEnUk;

interface EmployeeProject {
  id: string;
  name: string;
  color: string;
  totalBilledNet: number;
  performedAmount: number;
  budget?: {
    amount: number;
    approvedAt?: string;
  };
  previouslyApprovedBudget?: {
    amount: number;
  };
  totalOrderedAmount: number;
  workingPeriod: WorkingPeriod;
  isFavorite: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EmployeeProjectDetails',
  setup(__props) {

const { t } = useI18n<[MessageSchema], "en-UK">({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": { ...enUK, ...projectEnUk },
  },
});

const route = useRoute();

const { id } = route.params;
const projectId = Array.isArray(id) ? id[0] : id;

const { result } = useQuery<{ project: EmployeeProject }>(
  GET_EMPLOYEE_PROJECT,
  { id: projectId },
);

const project = computed(() => {
  const projectResult = result.value?.project;
  const totalPrice = projectResult?.workingPeriod?.orderedAmount;

  return {
    ...projectResult,
    price: totalPrice,
  };
});

const currentPeriod = computed(() => {
  const period = project.value?.workingPeriod;
  return period ? formatPeriodDate(period) : "";
});

const projectBudget = computed(() => {
  return project.value?.budget?.amount ?? 0;
});

const currentPerformed = computed(() => {
  return project.value?.workingPeriod?.performedAmount;
});

const orderedAmount = computed(
  () => project.value?.workingPeriod?.orderedAmount ?? 0,
);

const orderRatio = computed(() =>
  currentPerformed.value && orderedAmount.value
    ? currentPerformed.value / orderedAmount.value
    : 0,
);

const projectRatio = computed(() => {
  return totalSpent.value ? totalSpent.value / projectBudget.value : 0;
});

const totalSpent = computed(() => {
  return project?.value?.performedAmount ?? 0;
});

const timeLoggedResult = ref();
watch(
  project,
  (value) => {
    if (value?.workingPeriod) {
      console.log({ value });
      const startDate = dayjs(value.workingPeriod.startDate).format(
        "YYYY-MM-DD",
      );
      const endDate = dayjs(value.workingPeriod.endDate).format("YYYY-MM-DD");
      provideApolloClient(apolloClient);
      const { onResult } = useQuery<{
        loggedHours: LoggedHoursResult[];
      }>(GET_LOGGED_HOURS, {
        projectId,
        startDate,
        endDate,
      });
      onResult((res) => {
        timeLoggedResult.value = res.data;
      });
    }
  },
  { immediate: true },
);

const teamHours = computed(() => {
  return timeLoggedResult.value?.loggedHours;
});
const totalPlannedAmount = computed(() => {
  return teamHours.value?.reduce((acc, lh) => acc + lh.plannedAmount, 0);
});

const areDetailsVisible = ref(true);
const switchAreDetailsVisible = () => {
  areDetailsVisible.value = !areDetailsVisible.value;
};

const areThematicDetailsVisible = ref(true);
const switchAreThematicDetailsVisible = () => {
  areThematicDetailsVisible.value = !areThematicDetailsVisible.value;
};

const { mutate: updateFavorite, onError: onErrorUpdateFavorite } = useMutation(
  UPDATE_FAVORITE,
  {
    refetchQueries: [
      { query: GET_EMPLOYEE_PROJECT, variables: { id: projectId } },
    ],
  },
);

onErrorUpdateFavorite((error) => {
  logErrorMessages(error);
  useToast().error(
    t("errors.updateFavorite", { message: t("errors.internal") }),
  );
});

const toggleFavorite = () => {
  updateFavorite({
    projectId,
    value: !project.value.isFavorite,
  });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(MainContentBase, null, {
    default: _withCtx(() => [
      _createElementVNode("header", _hoisted_1, [
        _createVNode(IconButton, {
          "icon-name": "material-symbols:arrow-back",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$router.back()))
        }),
        _createElementVNode("h2", _hoisted_2, _toDisplayString(project.value.name), 1),
        _createVNode(IconButton, {
          "icon-name": project.value.isFavorite ? 'mdi:star' : 'mdi:star-outline',
          color: project.value.isFavorite ? 'primary' : undefined,
          onClick: toggleFavorite
        }, null, 8, ["icon-name", "color"])
      ]),
      _createVNode(BaseSheet, {
        class: "sheet",
        "tag-name": "section",
        title: _unref(t)('project.title')
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("h3", _hoisted_4, _toDisplayString(_unref(t)("labels.budget")), 1),
            (totalSpent.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(_unref(formatPrice)(totalSpent.value)) + " / ", 1))
              : _createCommentVNode("", true),
            _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(formatPrice)(projectBudget.value)), 1)
          ]),
          _createVNode(ProgressBar, { percentages: projectRatio.value }, null, 8, ["percentages"])
        ]),
        _: 1
      }, 8, ["title"]),
      _createVNode(BaseSheet, {
        class: "sheet",
        "tag-name": "section",
        title: currentPeriod.value
      }, {
        default: _withCtx(() => [
          _createVNode(ProgressBar, { percentages: orderRatio.value }, null, 8, ["percentages"]),
          _createElementVNode("ul", _hoisted_7, [
            _createElementVNode("li", _hoisted_8, [
              _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(t)("labels.budgetOrdered")), 1),
              _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(formatPrice)(orderedAmount.value)), 1)
            ]),
            _createElementVNode("li", _hoisted_11, [
              _createElementVNode("span", _hoisted_12, _toDisplayString(_unref(t)("labels.budgetLogged")), 1),
              (currentPerformed.value !== null && currentPerformed.value !== undefined)
                ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(_unref(formatPrice)(currentPerformed.value)), 1))
                : _createCommentVNode("", true),
              _createElementVNode("span", {
                class: _normalizeClass(['ratio', { danger: orderRatio.value > 0.8 }])
              }, _toDisplayString(_unref(formatPercentage)(orderRatio.value)), 3)
            ]),
            _createElementVNode("li", _hoisted_14, [
              _createElementVNode("span", _hoisted_15, _toDisplayString(_unref(t)("labels.budgetRemaining")), 1),
              _createElementVNode("span", {
                class: _normalizeClass(["value", {
              danger:
                orderedAmount.value - (currentPerformed.value || 0) < totalPlannedAmount.value,
            }])
              }, _toDisplayString(_unref(formatPrice)(orderedAmount.value - (currentPerformed.value || 0))), 3)
            ]),
            _createElementVNode("li", _hoisted_16, [
              _createElementVNode("span", _hoisted_17, _toDisplayString(_unref(t)("labels.budgetPlanned")), 1),
              _createElementVNode("span", _hoisted_18, _toDisplayString(_unref(formatPrice)(totalPlannedAmount.value)), 1)
            ])
          ])
        ]),
        _: 1
      }, 8, ["title"]),
      _createVNode(BaseSheet, {
        class: "sheet",
        "tag-name": "section"
      }, {
        header: _withCtx(() => [
          _createElementVNode("h3", {
            class: "sheet__title",
            onClick: switchAreThematicDetailsVisible
          }, [
            _createVNode(_unref(Icon), {
              icon: "entypo:triangle-right",
              rotate: areThematicDetailsVisible.value ? 1 : 0,
              class: "icon-invoices-menu"
            }, null, 8, ["rotate"]),
            _createTextVNode(" " + _toDisplayString(_unref(t)("project.reporting.thematic", { period: currentPeriod.value })), 1)
          ])
        ]),
        default: _withCtx(() => [
          (areThematicDetailsVisible.value)
            ? (_openBlock(), _createBlock(ProjectReportingThematic, {
                key: 0,
                id: _unref(projectId)
              }, null, 8, ["id"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createVNode(BaseSheet, {
        class: "sheet",
        "tag-name": "section"
      }, {
        header: _withCtx(() => [
          _createElementVNode("h3", {
            class: "sheet__title",
            onClick: switchAreDetailsVisible
          }, [
            _createVNode(_unref(Icon), {
              icon: "entypo:triangle-right",
              rotate: areDetailsVisible.value ? 1 : 0,
              class: "icon-invoices-menu"
            }, null, 8, ["rotate"]),
            _createTextVNode(" " + _toDisplayString(_unref(t)("project.hours.team.title", { period: currentPeriod.value })), 1)
          ])
        ]),
        default: _withCtx(() => [
          (areDetailsVisible.value)
            ? (_openBlock(), _createElementBlock("ul", _hoisted_19, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(teamHours.value, (teamMate) => {
                  return (_openBlock(), _createBlock(ProgressItem, {
                    key: teamMate.user.id,
                    title: teamMate.user.firstName,
                    max: `${teamMate.plannedHours}h`,
                    value: `${teamMate.loggedHours}h`,
                    percentage: teamMate.loggedHours / teamMate.plannedHours || 0
                  }, {
                    subtitle: _withCtx(() => [
                      _createElementVNode("span", _hoisted_20, _toDisplayString(_unref(formatFloat)(teamMate.loggedHours)) + "h", 1),
                      _createElementVNode("span", null, " /" + _toDisplayString(teamMate.plannedHours) + "h", 1)
                    ]),
                    _: 2
                  }, 1032, ["title", "max", "value", "percentage"]))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})